<template>
  <div>
    <div :class="{ error: !!error }">
      <label>
        <slot name="label" /> <span v-if="required">*</span>
        <span class="label-help">
          <slot name="labelHelp" />
        </span>
        <span class="ui-select">
          <span class="caret"><span class="l" /><span class="r" /></span>
          <select
            v-model="countryDialingCodeValue"
            name="countryDialingCode"
            autocomplete="country"
            autocorrect="off"
            @blur="handleCountryDialingCodeBlur"
            @change="changeCountryDialingCode($event.target?.value)"
          >
            <option
              v-for="country in topCountries"
              :key="country.alpha2"
              :value="country.alpha2"
            >
              {{ country.name }} (+{{ country.dialingCode }})
            </option>
            <option value="">---</option>
            <option
              v-for="country in countries"
              :key="country.alpha2"
              :value="country.alpha2"
            >
              {{ country.name }} (+{{ country.dialingCode }})
            </option>
          </select>
        </span>
      </label>

      <label class="phone-number-input">
        <input
          v-model="phoneNumberValue"
          autocomplete="tel-national"
          autocorrect="off"
          type="tel"
          :name="name"
          :required="required"
          :aria-label="t('Mobile phone number')"
          @input="changeNumber($event.target?.value)"
          @blur="handleNumberBlur"
        />
      </label>
      <input-error-message :has-error="!!error">
        <slot name="errorMessage" :error="error">
          <translate v-if="error === 'number'">
            Please check your phone number
          </translate>
          <translate v-else-if="error === 'required'">
            Please tell us your telephone number
          </translate>
          <span v-else>{{ error }}</span>
        </slot>
      </input-error-message>
      <p v-if="$slots.helpText" class="help-text">
        <slot name="helpText" />
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
/* eslint-disable @typescript-eslint/no-unsafe-return */

/* TODO: Add proper typing to resolve type safety warnings */

import { getUserCountry } from '~/utility/geo/geo-location'
import { useField } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import * as z from 'zod'
import { ValidatePhoneNumberRepositoryFetch } from '~/apps/phone-validation/ValidatePhoneNumberRepositoryFetch'
import { Country } from '~/stores/useCountriesStore'
import { isUndefined } from 'lodash-es'

export interface InternationalPhone {
  countryDialingCode: string
  number: string
  countryDialingNumber?: string
}

const props = defineProps<{
  name: string
  helpText?: string
  required: boolean
  modelValue: InternationalPhone
}>()

const { t } = useI18n()

const countriesStore = useCountriesStore()

const emit = defineEmits(['update:model-value', 'blur'])

const initialCountryDialingCode = ref('')

const serverError = ref()

const error = computed(
  () =>
    serverError.value ||
    countryDialingCodeErrors.value ||
    phoneNumberErrors.value,
)

watch(
  () => props.modelValue,
  (value) => {
    countryDialingCodeValue.value = value.countryDialingCode
    phoneNumberValue.value = value.number
  },
)

async function setCountryDialingCode() {
  const initialCode = props.modelValue?.countryDialingCode
  if (initialCode) {
    initialCountryDialingCode.value = initialCode
  } else {
    try {
      const userCountry = await getUserCountry()
      const currentCode = props.modelValue?.countryDialingCode
      initialCountryDialingCode.value = currentCode || userCountry

      emit('update:model-value', {
        ...props.modelValue,
        countryDialingCode: initialCountryDialingCode.value,
      })
    } catch (error) {
      console.error('Error fetching user country:', error)
    }
  }
}

const countryDialingCodeValidationSchema = computed(() =>
  toTypedSchema(
    props.required
      ? z.string({ required_error: 'required' }).min(1, { message: 'required' })
      : z.string().optional(),
  ),
)

const phoneNumberValidationSchema = computed(() => {
  const phoneRegex = /^[0-9 ]+$/ // only digits and spaces
  let schema: z.ZodSchema | undefined
  if (props.required) {
    schema = z
      .string({ required_error: 'required' })
      .min(1, { message: 'required' })
      .regex(phoneRegex, { message: 'number' })
  } else {
    // either a valid number or an empty string
    schema = z.union([
      z.string().regex(phoneRegex, { message: 'number' }),
      z.string().length(0).optional(),
    ])
  }
  return toTypedSchema(schema)
})

const {
  value: countryDialingCodeValue,
  errorMessage: countryDialingCodeErrors,
  validate: validateCountryDialingCode,
} = useField('countryDialingCode', countryDialingCodeValidationSchema.value, {
  validateOnValueUpdate: false,
  validateOnMount: false,
  initialValue: props.modelValue.countryDialingCode,
})

// TODO: vee-validate sees both countryDialingCode and number as two separate fields, we should model this as a single vee-validate field with name as props.name
const {
  value: phoneNumberValue,
  errorMessage: phoneNumberErrors,
  validate: validatePhoneNumber,
} = useField(props.name, phoneNumberValidationSchema.value, {
  initialValue: props.modelValue.number,
  validateOnValueUpdate: false,
  validateOnMount: false,
})

const currentLanguage = computed(() => {
  const lang = useRoute().params.lang
  const currentLang = typeof lang === 'string' ? lang : lang[0]
  return currentLang || 'en-gb'
})

onMounted(async () => {
  await setCountryDialingCode()
})

const topCountries: ComputedRef<Country[]> = computed(
  () => countriesStore.countries?.filter((c) => c.isTop === true) || [],
)

const countries: ComputedRef<Country[]> = computed(
  () => countriesStore.countries?.filter((c) => c.isTop === false) || [],
)

async function handleCountryDialingCodeBlur() {
  if (phoneNumberValue.value) {
    await validateNumber()
  }
}

async function handleNumberBlur() {
  await validateNumber()
}

async function validateNumber() {
  if (props.modelValue.number === '' || isUndefined(props.modelValue.number)) {
    serverError.value = ''
    return
  }

  if ((await validatePhoneNumber()) && (await validateCountryDialingCode())) {
    const repo = new ValidatePhoneNumberRepositoryFetch()
    try {
      await repo.validate(
        props.modelValue.countryDialingCode,
        props.modelValue.number,
        currentLanguage.value,
      )
      serverError.value = ''
    } catch (e) {
      serverError.value = e
    }
  }
}

async function changeNumber(phoneNumber: string) {
  await validatePhoneNumber()
  serverError.value = ''
  emit('update:model-value', {
    ...props.modelValue,
    number: phoneNumber,
  })
}

async function changeCountryDialingCode(eventCountryDialingCode: string) {
  await validateCountryDialingCode()
  serverError.value = ''
  emit('update:model-value', {
    ...props.modelValue,
    countryDialingCode: eventCountryDialingCode,
  })
}
</script>

<style scoped>
.phone-number-input {
  margin-top: 1em;
  display: block;
}
</style>
